import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { 
  Cpu, 
  ArrowRight 
} from 'lucide-react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';

const StatCard = ({ title, value, icon: Icon, color, loading }) => {
  const colorClasses = {
    success: 'text-green-600',
    info: 'text-cyan-600',
    warning: 'text-yellow-600',
    primary: 'text-blue-600'
  };
  
  return (
    <Card className="h-full border border-gray-200 rounded-lg">
      <CardContent className="p-4">
        <div className="flex justify-between items-start mb-4">
          <div className="text-sm text-gray-600">
            {loading ? <Skeleton className="w-20 h-4" /> : title}
          </div>
          {loading ? (
            <Skeleton className="w-6 h-6 rounded-full" />
          ) : (
            <Icon className={`w-5 h-5 ${colorClasses[color]}`} />
          )}
        </div>
        {loading ? (
          <Skeleton className="w-16 h-8" />
        ) : (
          <div className="text-2xl font-medium text-gray-900">
            {value}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default function EdgeDevicesWidget({ loading = false }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // Mock data - in a real app, this would come from an API
  const edgeDevicesData = {
    active: 8,
    processing: "24.5 TB",
    avgCpu: "42%",
    totalDevices: 12
  };
  
  const handleNavigateToEdgeDevices = () => {
    navigate('/dashboard/edge-devices');
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2, duration: 0.5 }}
      className="h-full"
    >
      <Card className="h-full rounded-xl border border-gray-200 overflow-visible relative">
        <CardContent className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-semibold text-gray-900">
              {loading ? <Skeleton className="w-32 h-6" /> : t('dashboard.edge.title')}
            </h3>
            <Button 
              variant="ghost"
              size="sm"
              onClick={handleNavigateToEdgeDevices}
              disabled={loading}
              className="text-blue-600 hover:text-blue-700"
            >
              {loading ? <Skeleton className="w-20 h-4" /> : (
                <>
                  {t('dashboard.general.viewAll', 'View All')}
                  <ArrowRight className="w-4 h-4 ml-1" />
                </>
              )}
            </Button>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            <StatCard 
              title={t('dashboard.edge.active')} 
              value={edgeDevicesData.active} 
              icon={Cpu}
              color="success"
              loading={loading}
            />
            <StatCard 
              title={t('dashboard.edge.processing')} 
              value={edgeDevicesData.processing} 
              icon={Cpu}
              color="info"
              loading={loading}
            />
            <StatCard 
              title={t('dashboard.edge.avgCpu')} 
              value={edgeDevicesData.avgCpu} 
              icon={Cpu}
              color="warning"
              loading={loading}
            />
            <StatCard 
              title={t('dashboard.edge.totalDevices')} 
              value={edgeDevicesData.totalDevices} 
              icon={Cpu}
              color="primary"
              loading={loading}
            />
          </div>
          
          {!loading && (
            <div className="mt-6">
              <div className="text-sm text-gray-600 mb-2">
                CPU Usage
              </div>
              <Progress 
                value={42} 
                className="h-2 bg-gray-100"
              />
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
} 